<script>
import { authMethods, notificationMethods } from "@/state/helpers";
import {
  required,
  minLength,
  maxLength,
  sameAs,
} from "vuelidate/lib/validators";
import { errorMessages } from "@/helpers/validations/errorMessages";
import InstitutionalLogosBanner from "@/components/institutional-logos-banner.vue";
import AppBrandLogo from "@/components/AppBrandLogo.vue";
import {
  loadSiteIdentity,
  applyLoginBannerCssVariable,
} from "@/helpers/site-identity/site-identity-api.js";

export default {
  components: {
    InstitutionalLogosBanner,
    AppBrandLogo,
  },
  data() {
    return {
      password: "",
      repassword: "",
      email: "",
      token: "",
      submitted: false,
      errorMsg: null,
      tryingToReset: false,
      isResetError: false,
      loading: false,
      errorMessages,
      showPassword: false,
      showRePassword: false,
    };
  },
  validations: {
    password: { required, minLength: minLength(8), maxLength: maxLength(20) },
    repassword: { required, sameAs: sameAs("password") },
  },
  async created() {
    document.body.classList.add("auth-body-bg");
  },
  async mounted() {
    const identity = await loadSiteIdentity();
    applyLoginBannerCssVariable(identity?.login_banner);

    try {
      const params = this.$route.query;
      this.email = params.email;
      this.token = params.token;
      await this.validateResetToken({
        email: params.email,
        token: params.token,
      });
    } catch (error) {
      this.errorMsg = error?.response?.data?.errors[0]?.title;
      this.error(this.errorMsg);
      this.$router.push({ name: "login" });
    }
  },
  methods: {
    ...authMethods,
    ...notificationMethods,
    togglePasswordVisibility() {
      this.showPassword = !this.showPassword;
    },
    toggleRePasswordVisibility() {
      this.showRePassword = !this.showRePassword;
    },
    // Try to register the user in with the email, fullname
    // and password they provided.
    async tryToReset() {
      this.submitted = true;
      // stop here if form is invalid
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }
      this.loading = true;
      this.tryingToReset = true;
      // Reset the authError if it existed.
      this.errorMsg = null;
      try {
        await this.resetPassword({
          token: this.token,
          email: this.email,
          password: this.password,
          re_password: this.repassword,
        });
        this.loading = false;
        this.success("Contraseña cambiada correctamente.");
        this.$router.push({ name: "login" });
      } catch (error) {
        this.loading = false;
        console.log(error);
      }
    },
  },
};
</script>

<template>
  <div class="auth-page-wrapper">
    <!-- Background decorative elements -->
    <div class="auth-background">
      <div class="auth-shape auth-shape-1"></div>
      <div class="auth-shape auth-shape-2"></div>
      <div class="auth-shape auth-shape-3"></div>
    </div>

    <!-- Main content -->
    <div class="auth-container">
      <div class="auth-card">
        <!-- Logo Section -->
        <div class="auth-logo-section">
          <div class="auth-logo-wrapper">
            <AppBrandLogo class-name="auth-main-logo" />
          </div>
          <div class="auth-icon-wrapper">
            <i class="ri-lock-unlock-line auth-icon-large"></i>
          </div>
          <h1 class="auth-welcome-title">Restablecer contraseña</h1>
          <p class="auth-welcome-subtitle">
            Ingresa tu nueva contraseña para continuar
          </p>
        </div>

        <!-- Alert Messages -->
        <b-alert
          variant="danger"
          class="auth-alert"
          v-if="errorMsg"
          show
          dismissible
        >
          {{ errorMsg }}
        </b-alert>

        <!-- Reset Form -->
        <form class="auth-form" @submit.prevent="tryToReset">
          <!-- Password Field -->
          <div class="auth-form-group">
            <label for="password" class="auth-label">
              <i class="ri-lock-2-line auth-label-icon"></i>
              Nueva contraseña
            </label>
            <div class="auth-input-wrapper">
              <input
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                class="auth-input"
                id="password"
                placeholder="Mínimo 8 caracteres"
                :class="{
                  'auth-input-error': submitted && $v.password.$error,
                }"
              />
              <button
                type="button"
                class="auth-password-toggle"
                @click="togglePasswordVisibility"
              >
                <i
                  :class="showPassword ? 'ri-eye-off-line' : 'ri-eye-line'"
                ></i>
              </button>
              <div
                v-if="submitted && $v.password.$error"
                class="auth-error-message"
              >
                <i class="ri-error-warning-line"></i>
                <span v-if="!$v.password.required">{{
                  errorMessages.required
                }}</span>
                <span v-else-if="!$v.password.minLength"
                  >Debe tener al menos
                  {{
                    $v.password.$params.minLength.min
                  }}
                  caracteres.</span
                >
                <span v-else-if="!$v.password.maxLength"
                  >No puede tener más de
                  {{
                    $v.password.$params.maxLength.max
                  }}
                  caracteres.</span
                >
              </div>
            </div>
          </div>

          <!-- Confirm Password Field -->
          <div class="auth-form-group">
            <label for="repassword" class="auth-label">
              <i class="ri-lock-password-line auth-label-icon"></i>
              Confirmar contraseña
            </label>
            <div class="auth-input-wrapper">
              <input
                v-model="repassword"
                :type="showRePassword ? 'text' : 'password'"
                class="auth-input"
                id="repassword"
                placeholder="Confirma tu contraseña"
                :class="{
                  'auth-input-error': submitted && $v.repassword.$error,
                }"
              />
              <button
                type="button"
                class="auth-password-toggle"
                @click="toggleRePasswordVisibility"
              >
                <i
                  :class="showRePassword ? 'ri-eye-off-line' : 'ri-eye-line'"
                ></i>
              </button>
              <div
                v-if="submitted && $v.repassword.$error"
                class="auth-error-message"
              >
                <i class="ri-error-warning-line"></i>
                <div v-if="!$v.repassword.required">
                  {{ errorMessages.required }}
                </div>
                <div v-else-if="!$v.repassword.sameAs">
                  {{ errorMessages.sameAs }}
                </div>
              </div>
            </div>
          </div>

          <!-- Password Requirements -->
          <div class="auth-password-requirements">
            <p class="auth-requirements-title">La contraseña debe contener:</p>
            <ul class="auth-requirements-list">
              <li :class="{ 'auth-requirement-met': password.length >= 8 }">
                <i :class="password.length >= 8 ? 'ri-check-line' : 'ri-close-line'"></i>
                Mínimo 8 caracteres
              </li>
              <li :class="{ 'auth-requirement-met': password.length <= 20 && password.length >= 8 }">
                <i :class="password.length <= 20 && password.length >= 8 ? 'ri-check-line' : 'ri-close-line'"></i>
                Máximo 20 caracteres
              </li>
              <li :class="{ 'auth-requirement-met': repassword && $v.repassword.sameAs }">
                <i :class="repassword && $v.repassword.sameAs ? 'ri-check-line' : 'ri-close-line'"></i>
                Las contraseñas coinciden
              </li>
            </ul>
          </div>

          <!-- Submit Button -->
          <button
            class="auth-submit-btn"
            type="submit"
            :disabled="loading"
          >
            <span v-if="loading" class="auth-spinner"></span>
            <span v-else>
              <i class="ri-check-line"></i>
              Cambiar contraseña
            </span>
          </button>
        </form>

        <!-- Back to Login Link -->
        <div class="auth-footer">
          <router-link
            tag="a"
            to="/login"
            class="auth-link auth-link-back"
          >
            <i class="ri-arrow-left-line"></i>
            Volver al inicio de sesión
          </router-link>
        </div>
      </div>

      <!-- Decorative side panel -->
      <div class="auth-side-panel">
        <div class="auth-side-content">
          <div class="auth-side-logo">
            <InstitutionalLogosBanner variant="default" />
          </div>
          <h2 class="auth-side-title">Nueva contraseña</h2>
          <p class="auth-side-description">
            Crea una contraseña segura para proteger tu cuenta
          </p>
          <div class="auth-side-features">
            <div class="auth-feature-item">
              <i class="ri-shield-star-line"></i>
              <span>Contraseña encriptada</span>
            </div>
            <div class="auth-feature-item">
              <i class="ri-key-line"></i>
              <span>Alta seguridad</span>
            </div>
            <div class="auth-feature-item">
              <i class="ri-lock-password-line"></i>
              <span>Protección garantizada</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.auth-icon-wrapper {
  margin: 1.5rem 0;
  display: flex;
  justify-content: center;
}

.auth-icon-large {
  font-size: 4rem;
  color: #6ea637;
  opacity: 0.8;
}

.auth-password-requirements {
  background: rgba(110, 166, 55, 0.05);
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 0.5rem;
}

.auth-requirements-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #558b2f;
  margin: 0 0 0.75rem 0;
}

.auth-requirements-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.auth-requirements-list li {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #5a6c5d;

  i {
    font-size: 1rem;
  }

  &.auth-requirement-met {
    color: #6ea637;
    font-weight: 500;

    i {
      color: #6ea637;
    }
  }

  &:not(.auth-requirement-met) i {
    color: #e74c3c;
  }
}

.auth-link-back {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
}
</style>
